import React, { useEffect, useMemo } from 'react';
import { EHttpMethod, usePortalQuery } from '../hooks/usePortalQuery';
import { Appointment, Contract, CustomerGroup, EMpanAssignmentMode, EMpanEditMode, MeterPointDetails } from '../types';
import { FormSearchDropdown } from '@portal_components/components/FormSearchDropdown';
import { FormDropdown } from '@portal_components/components/FormDropdown';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { MeterPointDetailsForm } from './MeterPointDetailsForm/MeterPointDetailsForm';
import { FormRadioButtonGroup } from '@portal_components/components/FormRadioButtonGroup';
import { DateTime } from 'luxon';

interface MeterPointDetailsFormWrapperProps {
  mode: EMpanAssignmentMode;
  mpan: string;
  meterPointDetailsForMpan?: { [customerGroupId: string]: { [contractId: string]: MeterPointDetails } };
  handleReset: () => void;
}

const validationSchema = Yup.object<
  { contractId: string; customerGroupId: string },
  {
    [Key in keyof { contractId: string; customerGroupId: string }]: Yup.AnySchema<{ contractId: string; customerGroupId: string }[Key]>;
  }
>({
  customerGroupId: Yup.string().required(),
  contractId: Yup.string().required()
});

export const MeterPointDetailsFormWrapper = ({ mode, mpan, meterPointDetailsForMpan, handleReset }: MeterPointDetailsFormWrapperProps) => {
  const { data: customerGroups, isLoading: customerGroupsLoading } = usePortalQuery<CustomerGroup[]>(
    EHttpMethod.GET,
    'customer-groups',
    'customer-groups',
    true,
    undefined,
    undefined,
    undefined,
    undefined,
    600000,
    600000
  );

  const methods = useForm<{ contractId: string; customerGroupId: string; editMode?: EMpanEditMode }>({
    values: {
      contractId: '',
      customerGroupId: '',
      editMode: mode === EMpanAssignmentMode.EDIT ? EMpanEditMode.EDIT : undefined
    },
    resolver: yupResolver<{ contractId: string; customerGroupId: string; editMode?: EMpanEditMode }>(validationSchema as any)
  });

  const [customerGroupId, contractId, editMode] = methods.watch(['customerGroupId', 'contractId', 'editMode']);

  useEffect(() => {
    if (!customerGroupId) return;
    methods.setValue('contractId', '');
  }, [customerGroupId, methods]);

  //only filter for creation of new sites. lookups for search/edit should not be affected.
  const filterContracts = mode === EMpanAssignmentMode.NEW ? 'Y' : 'N';

  const { data: customerContracts, isLoading: customerContractsLoading } = usePortalQuery<Contract[]>(
    EHttpMethod.GET,
    `customer-contracts/${customerGroupId}?filter=${filterContracts}`,
    `customer-contracts/${customerGroupId}?filter=${filterContracts}`,
    !!customerGroupId
  );

  const { data: appointment, isLoading: appointmentLoading } = usePortalQuery<Appointment>(
    EHttpMethod.GET,
    `get-appointment/${mpan}/${contractId}`,
    `get-appointment/${mpan}-${contractId}`,
    !!customerGroupId && !!contractId && !!mpan && mode === EMpanAssignmentMode.NEW
  );

  const customerGroupIdOptions = useMemo(() => {
    if (!customerGroups) return [];
    if (mode !== EMpanAssignmentMode.NEW && !meterPointDetailsForMpan) return [];
    const options = customerGroups.map(({ customerGroupName, customerGroupId }) => ({
      title: customerGroupName,
      value: customerGroupId
    }));
    if (mode === EMpanAssignmentMode.NEW || !meterPointDetailsForMpan) return options;
    return options.filter(({ value }) => !!meterPointDetailsForMpan[value]);
  }, [customerGroups, meterPointDetailsForMpan, mode]);

  const contractIdOptions = useMemo(() => {
    if (!customerContracts) return [];
    if (mode !== EMpanAssignmentMode.NEW && !meterPointDetailsForMpan) return [];
    const options = customerContracts.map(({ contractId, contractType }) => ({
      title: `${contractType} (Contract ID: ${contractId})`,
      value: contractId
    }));
    if (mode === EMpanAssignmentMode.NEW || !meterPointDetailsForMpan) return options;
    return options.filter(({ value }) => customerGroupId && !!meterPointDetailsForMpan[customerGroupId][value]);
  }, [customerContracts, mode, meterPointDetailsForMpan, customerGroupId]);

  const fieldReadonly = useMemo(() => {
    return !!contractId && !!customerGroupId && mode === EMpanAssignmentMode.EDIT;
  }, [contractId, customerGroupId, mode]);

  const recordIsDuplicate = useMemo(() => {
    return !!contractId && !!customerGroupId && !!appointment && mode === EMpanAssignmentMode.NEW && appointment.existingContracts?.includes(Number(contractId));
  }, [appointment, contractId, customerGroupId, mode]);

  const hasOpenExceptions = useMemo(() => {
    if (mode !== EMpanAssignmentMode.NEW) return false;
    return !!appointment && !appointmentLoading && appointment.hasOpenExceptions;
  }, [appointment, appointmentLoading, mode]);

  const contractStartDate = useMemo(() => {
    if (!contractId || !customerContracts) return undefined;
    const selectedContract = customerContracts.find(contract => contract.contractId === contractId);
    if (!selectedContract) return undefined;
    return DateTime.fromISO(selectedContract.contractStartDate).toUTC(undefined, { keepLocalTime: true }).startOf('day');
  }, [contractId, customerContracts]);

  const defaultEffectiveDate = useMemo(() => {
    const currentDate = DateTime.now().toUTC(undefined, { keepLocalTime: true }).startOf('day');
    if (contractStartDate) {
      if (contractStartDate.isValid && contractStartDate > currentDate) {
        return contractStartDate.toISO({ suppressMilliseconds: true });
      }
    }
    return (
      appointment?.effectiveDate ??
      currentDate.toISO({
        suppressMilliseconds: true
      })
    );
  }, [appointment?.effectiveDate, contractStartDate]);

  const selectedContractTypeCode = useMemo(() => {
    if (!contractId || !customerContracts) return undefined;
    const selectedContract = customerContracts.find(contract => contract.contractId === contractId);
    if (!selectedContract) return undefined;
    return selectedContract.contractTypeCode;
  }, [contractId, customerContracts]);

  if (customerGroupsLoading) {
    return <div className="flex flex-row justify-center">Loading customer groups...</div>;
  }

  return (
    <div>
      <FormProvider {...methods}>
        <form>
          <FormSearchDropdown title="Customer Group" name="customerGroupId" placeholder="Select Customer Group" elementsForDisplay={customerGroupIdOptions} readonly={fieldReadonly} mandatory />
          {!customerContractsLoading && <FormDropdown title="Contract" name="contractId" placeholder="Select Contract" elementsForDisplay={contractIdOptions} readonly={fieldReadonly} mandatory />}
          {!!contractId && mode === EMpanAssignmentMode.EDIT && (
            <FormRadioButtonGroup
              title="Select Edit Operation"
              name="editMode"
              buttons={[
                { title: 'Edit', value: EMpanEditMode.EDIT },
                { title: 'Transfer', value: EMpanEditMode.TRANSFER },
                { title: 'Terminate', value: EMpanEditMode.TERMINATE }
              ]}
            />
          )}
        </form>
      </FormProvider>
      {customerGroupId && customerContractsLoading && <div className="flex flex-row justify-center">Loading contracts...</div>}
      {!customerContractsLoading && customerGroupId && (!customerContracts || customerContracts.length === 0 || contractIdOptions.length === 0) && (
        <div className="pb-4 text-feedback-red inline-block">{'This customer group has no active contracts.'}</div>
      )}
      {customerGroupId && contractId && !customerContractsLoading && (
        <div className="flex flex-row justify-left text-lg text-feedback-red">
          {recordIsDuplicate && !hasOpenExceptions && (
            <p>This site is already assigned to the currently selected Customer/Contract ID. Please use the EDIT function to make updates to this site or select a different customer.</p>
          )}
          {hasOpenExceptions && !recordIsDuplicate && <p>There are open exceptions related to this MPAN. Please resolve these before re-attempting site creation.</p>}
          {hasOpenExceptions && recordIsDuplicate && (
            <p>This site is already assigned to the currently selected Customer/Contract ID and there are open exceptions related to this MPAN. Please resolve these before proceeding.</p>
          )}
        </div>
      )}
      {mode === EMpanAssignmentMode.NEW && appointmentLoading && !!contractId && !!customerGroupId && <div className="flex flex-row justify-center">Loading appointment...</div>}
      {mode === EMpanAssignmentMode.NEW && !appointmentLoading && !appointment?.active && <div className="flex flex-row justify-center py-4">No Valid MOP Appointment for MPAN: {mpan}.</div>}
      {!!contractId && !!customerGroupId && !hasOpenExceptions && !recordIsDuplicate && ((mode === EMpanAssignmentMode.NEW && !appointmentLoading) || mode !== EMpanAssignmentMode.NEW) && (
        <MeterPointDetailsForm
          mpan={mpan}
          mode={mode}
          editMode={editMode}
          onSubmit={handleReset}
          address={appointment?.address}
          contact={appointment?.contact}
          effectiveDate={defaultEffectiveDate}
          hasActiveAppointment={!!appointment?.active}
          customerGroupId={customerGroupId}
          contractId={contractId}
          contractTypeCode={selectedContractTypeCode}
          contractStartDate={contractStartDate}
          existingMeterPointDetails={meterPointDetailsForMpan && contractId && customerGroupId ? meterPointDetailsForMpan[customerGroupId][contractId] : undefined}
        />
      )}
    </div>
  );
};
